<template>
  <div class="page-container">
    <div class="page-title-container d-flex align-items-center justify-content-between">
      <h1 class="page-title mb-0">Confirmaciones por WhatsApp</h1>
      <span v-if="testMode" class="test-badge">🧪 MODO PRUEBA</span>
    </div>

    <!-- ═══════════ Filtros ═══════════ -->
    <div class="table-container mb-3">
      <div class="table-header">
        <div class="w-full d-flex flex-wrap align-items-center">
          <div class="d-flex align-items-center mr-3 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2" for="conf-date">Fecha</label>
            <input id="conf-date" type="date" v-model="date" class="toolbar-select" />
          </div>

          <div class="d-flex align-items-center mr-3 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2" for="conf-city">Ciudad</label>
            <select id="conf-city" v-model="cityId" class="toolbar-select">
              <option :value="null">Todas</option>
              <option v-for="c in cities" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>

          <label class="test-toggle mr-3 mb-2 mb-md-0">
            <input type="checkbox" v-model="testMode" />
            <span>Modo prueba</span>
          </label>

          <div v-if="testMode" class="d-flex align-items-center mr-3 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2" for="conf-phone">Teléfono de prueba</label>
            <input
              id="conf-phone"
              type="text"
              v-model="testPhone"
              placeholder="+51..."
              class="toolbar-select toolbar-select--text"
            />
          </div>

          <b-button variant="none" class="button button-primary mb-2 mb-md-0" :disabled="!date" @click="search">
            Buscar viajes pendientes
          </b-button>
        </div>

        <p v-if="testMode" class="test-hint mt-2 mb-0">
          Todos los mensajes se enviarán a este número. Los viajes no se marcarán como confirmados.
        </p>
      </div>
    </div>

    <!-- ═══════════ Resultados ═══════════ -->
    <template v-if="searched">
      <div class="row mb-3">
        <div class="col-4 mb-2" v-for="c in summaryCards" :key="c.key">
          <div class="summary-card" :class="c.key">
            <span class="summary-card__label">{{ c.label }}</span>
            <span class="summary-card__value">{{ c.value }}</span>
          </div>
        </div>
      </div>

      <div class="table-container">
        <div class="table-header d-flex flex-wrap justify-content-between align-items-center">
          <h3 class="m-0">{{ counterText }}</h3>
          <b-button
            variant="none"
            class="button"
            :class="testMode ? 'button-test' : 'button-primary'"
            :disabled="!selected.length || sending"
            @click="confirmSend"
          >
            <span v-if="sending">Enviando…</span>
            <span v-else-if="testMode">🧪 Enviar {{ selected.length }} prueba{{ selected.length === 1 ? '' : 's' }} a {{ testPhone || '—' }}</span>
            <span v-else>Enviar {{ selected.length }} confirmaci{{ selected.length === 1 ? 'ón' : 'ones' }}</span>
          </b-button>
        </div>

        <div class="table-responsive mb-0">
          <table class="custom-table w-100">
            <thead>
              <tr>
                <th class="pl-4 text-center col-check">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    :disabled="!selectableIds.length"
                    @change="toggleAll"
                  />
                </th>
                <th class="text-left">Ride</th>
                <th class="text-left">Pasajero</th>
                <th class="text-left">Teléfono</th>
                <th class="text-center">Tipo viaje</th>
                <th class="text-center">Hora</th>
                <th class="text-left">Plantilla</th>
                <th class="text-center">Idioma</th>
                <th class="text-left">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in rows" :key="r.id" :class="rowClass(r)">
                <td class="pl-4 text-center">
                  <input
                    type="checkbox"
                    :value="r.id"
                    v-model="selected"
                    :disabled="!!r.validation_error || isDone(r)"
                  />
                </td>
                <td class="ride-code">{{ r.ride_code }}</td>
                <td>{{ r.passenger_name || '—' }}</td>
                <td>
                  <div>{{ r.phone_e164 || '—' }}</div>
                  <div v-if="r.phone !== r.phone_e164" class="text-muted small">en BD: {{ r.phone || '—' }}</div>
                </td>
                <td class="text-center">
                  <div>{{ r.trip_type }}</div>
                  <div v-if="r.flight_type" class="text-muted small">{{ r.flight_type }}</div>
                </td>
                <td class="text-center">{{ formatHora(r.pickup_time) }}</td>
                <td class="template-cell">{{ r.template_name || '—' }}</td>
                <td class="text-center">
                  <span v-if="r.template_name" class="badge badge-gray">{{ r.detected_language.toUpperCase() }}</span>
                  <span v-else>—</span>
                </td>
                <td>
                  <!-- Resultado del envío, si ya se ejecutó -->
                  <template v-if="results[r.id]">
                    <span :class="`badge ${resultMeta(results[r.id]).color}`">
                      {{ resultMeta(results[r.id]).label }}
                    </span>
                    <div v-if="results[r.id].chatwoot_conversation_id" class="row-messages">
                      conversación #{{ results[r.id].chatwoot_conversation_id }}
                    </div>
                    <div v-if="results[r.id].error" class="row-messages row-messages--error">
                      {{ results[r.id].error }}
                    </div>
                  </template>
                  <template v-else-if="r.validation_error">
                    <span class="badge badge-yellow">Con error</span>
                    <div class="row-messages row-messages--error">
                      <i class="ri-error-warning-line"></i> {{ r.validation_error }}
                    </div>
                  </template>
                  <span v-else class="badge badge-green">Listo</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-footer d-flex justify-content-end align-items-center">
          <div class="table-no-results" v-if="!rows.length">
            <p class="m-0 px-3">No hay viajes pendientes de confirmación para esa fecha.</p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'

import { ConfirmationService, GeographyService } from '@/core/services'

// Pantalla de envío de confirmaciones (DEF-ADM-024). Reemplaza los dos
// workflows de n8n que leían de Google Sheets: acá el operador ve antes de
// enviar qué viaje va a salir, con qué plantilla y cuál no se puede mandar.
export default {
  name: 'ConfirmationsPage',
  data() {
    return {
      date: this.tomorrow(),
      cityId: null,
      cities: [],
      testMode: false,
      testPhone: '',
      rows: [],
      summary: { total: 0, ready: 0, with_errors: 0 },
      selected: [],
      // Resultado del último envío, indexado por booking_id.
      results: {},
      searched: false,
      sending: false
    }
  },
  computed: {
    // Solo las filas sin error de validación se pueden enviar.
    selectableIds() {
      return this.rows.filter((r) => !r.validation_error && !this.isDone(r)).map((r) => r.id)
    },
    allSelected() {
      return this.selectableIds.length > 0 && this.selected.length === this.selectableIds.length
    },
    summaryCards() {
      return [
        { key: 'total', label: 'Viajes', value: this.summary.total },
        { key: 'ready', label: 'Listos para enviar', value: this.summary.ready },
        { key: 'errors', label: 'Con errores', value: this.summary.with_errors }
      ]
    },
    counterText() {
      const r = this.summary.ready
      const e = this.summary.with_errors
      const base = `${r} viaje${r === 1 ? '' : 's'} listo${r === 1 ? '' : 's'} para enviar`
      return e ? `${base}, ${e} con error${e === 1 ? '' : 'es'}` : base
    }
  },
  methods: {
    tomorrow() {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      return d.toISOString().slice(0, 10)
    },
    formatHora(h) {
      if (!h) return '—'
      return h.length >= 5 ? h.slice(0, 5) : h
    },
    // Una fila ya enviada (o en prueba) no se vuelve a seleccionar.
    isDone(r) {
      const res = this.results[r.id]
      return !!res && res.status !== 'ERROR'
    },
    rowClass(r) {
      const res = this.results[r.id]
      if (res && res.status === 'ERROR') return 'row-error'
      if (res) return 'row-sent'
      return r.validation_error ? 'row-warning' : ''
    },
    resultMeta(res) {
      if (res.status === 'SENT') return { label: 'Enviado', color: 'badge-green' }
      if (res.status === 'TEST') return { label: 'Prueba', color: 'badge-yellow' }
      return { label: 'Error', color: 'badge-red' }
    },
    toggleAll(e) {
      this.selected = e.target.checked ? [...this.selectableIds] : []
    },
    async loadCities() {
      try {
        const res = await GeographyService.listCities()
        this.cities = (res.payload || []).filter((c) => c.is_active)
      } catch (e) {
        // Sin ciudades el desplegable queda en "Todas", que sigue sirviendo.
        this.cities = []
      }
    },
    async search() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await ConfirmationService.pending(this.date, this.cityId)
        this.rows = res.payload.data || []
        this.summary = res.payload.summary
        this.results = {}
        // Arranca con todos los válidos marcados: es lo que el operador quiere
        // el 99 % de las veces y puede destildar los que no.
        this.selected = [...this.selectableIds]
        this.searched = true
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudieron obtener los viajes pendientes')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    confirmSend() {
      if (!this.selected.length) return

      if (this.testMode && !this.testPhone.trim()) {
        Alert.error('Indica el teléfono de prueba antes de enviar')
        return
      }

      const n = this.selected.length
      const message = this.testMode
        ? `Enviar ${n} mensaje${n === 1 ? '' : 's'} de PRUEBA al número ${this.testPhone}. No se marcarán como confirmados.`
        : `¿Enviar confirmación por WhatsApp a ${n} pasajero${n === 1 ? '' : 's'}?`

      this.$bvModal
        .msgBoxConfirm(message, {
          title: this.testMode ? 'Confirmar envío de prueba' : 'Confirmar envío',
          okTitle: 'Enviar',
          cancelTitle: 'Cancelar',
          okVariant: this.testMode ? 'warning' : 'primary',
          centered: true
        })
        .then((ok) => {
          if (ok) this.send()
        })
    },
    async send() {
      this.sending = true
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await ConfirmationService.send(this.selected, {
          testMode: this.testMode,
          testPhone: this.testMode ? this.testPhone.trim() : null
        })

        const payload = res.payload
        const map = {}
        ;(payload.results || []).forEach((r) => {
          map[r.booking_id] = r
        })
        this.results = map
        // Lo enviado deja de estar seleccionado; lo que falló queda para reintentar.
        this.selected = (payload.results || [])
          .filter((r) => r.status === 'ERROR')
          .map((r) => r.booking_id)

        const s = payload.summary
        const done = s.sent + s.tested
        if (s.errors) {
          Alert.error(`${done} enviado(s), ${s.errors} con error. Revisa el detalle en la tabla.`)
        } else {
          Alert.success(`${done} confirmación(es) enviada(s) correctamente`)
        }
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo ejecutar el envío')
      } finally {
        this.sending = false
        await this.$store.dispatch('app/loading', false)
      }
    }
  },
  async mounted() {
    await this.loadCities()
  }
}
</script>

<style lang="stylus" scoped>
.toolbar-select
  height 34px
  padding 0 10px
  font-size 0.8rem
  border 1px solid #d1d5db
  border-radius 6px
  background-color #fff
  color #374151
  &--text
    width 150px

.filter-label
  font-size 0.8rem
  color #6b7280
  white-space nowrap

.test-badge
  background-color #fff7ed
  border 1px solid #fdba74
  color #c2410c
  font-size 0.78rem
  font-weight 600
  padding 4px 12px
  border-radius 999px

.test-toggle
  display flex
  align-items center
  gap 6px
  font-size 0.82rem
  color #374151
  cursor pointer
  margin-bottom 0

.test-hint
  font-size 0.78rem
  color #c2410c

.button-test
  background-color #ea580c
  border-color #ea580c
  color #fff
  &:hover:not(:disabled)
    background-color #c2410c

.summary-card
  display flex
  flex-direction column
  gap 4px
  padding 12px 16px
  border 0.3px solid #e5e7eb
  border-radius 6px
  background-color #fff
  &__label
    font-size 11px
    text-transform uppercase
    letter-spacing .04em
    color #6c757d
  &__value
    font-size 1.3rem
    font-weight 600
  &.ready &__value
    color #15803d
  &.errors &__value
    color #c2410c

.ride-code
  font-weight 600

.template-cell
  font-size 0.78rem
  color #4b5563
  word-break break-all

.row-warning
  background-color #fffbeb
.row-error
  background-color #fef2f2
.row-sent
  background-color #f0fdf4

.row-messages
  margin 4px 0 0
  font-size 0.74rem
  color #6b7280
  &--error
    color #b91c1c

.col-check
  width 44px
</style>
